import React, { useEffect, useRef, useState } from "react";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";

import { CustomerAPI, NonFieldFeatureAPI, NFF_POPUP_ACTION } from "@ai360/core";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { actions as cdActions, selectors as cdSelectors } from "~/customer-data";
import { getUser } from "~/login";
import { setActiveModule, ActionPanelModuleList } from "~/action-panel";
import * as nonFieldFeatureInformationActions from "~/action-panel/components/layer-module/components/non-field-feature-information/actions";
import * as samplingActions from "~/action-panel/components/layer-module/components/sampling/actions";
import { DeleteConfDialog } from "~/action-panel/components/context-menus/item-delete-confirmation-dialog";
import { messages as contextMenuMessages } from "~/action-panel/components/context-menus/i18n-messages";
import { messages as globalMessages } from "~/i18n-messages";
import { logFirebaseEvent } from "~/utils/firebase";

interface INonFieldFeaturePopupActionHandlerProps {
    map: __esri.MapView;
}

export const NonFieldFeaturePopupActionHandler = ({
    map,
}: INonFieldFeaturePopupActionHandlerProps) => {
    const dispatch = useAppDispatch();
    const nonFieldFeatures = useAppSelector(cdSelectors.getVisibleNonFieldFeatures);
    const userInfo = useAppSelector(getUser);

    const [pendingDeleteFeature, setPendingDeleteFeature] =
        useState<NonFieldFeatureAPI.NonFieldFeature | null>(null);

    const onTriggerActionRef = useRef<(event: { action: { id: string } }) => void>();
    onTriggerActionRef.current = (event) => {
        const featureId = (map.popup?.selectedFeature?.attributes as { id?: string })?.id;
        if (!featureId) {
            return;
        }
        const feature = nonFieldFeatures.find((f) => f.id === featureId);
        if (!feature) {
            return;
        }
        const role = userInfo?.role;
        switch (event.action.id) {
            case NFF_POPUP_ACTION.EDIT:
                dispatch(
                    nonFieldFeatureInformationActions.startEditingFeature(feature, {
                        customerGuid: feature.customerId,
                        name: feature.customerName,
                    } as CustomerAPI.ICustomer)
                );
                map.popup?.close();
                break;
            case NFF_POPUP_ACTION.ADD_SAMPLE:
                if (role?.water) {
                    dispatch(setActiveModule(ActionPanelModuleList.LAYER));
                    dispatch(samplingActions.addSampling(feature));
                }
                map.popup?.close();
                break;
            case NFF_POPUP_ACTION.DELETE:
                setPendingDeleteFeature(feature);
                map.popup?.close();
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        if (!map) {
            return undefined;
        }
        const handle = reactiveUtils.on(
            () => map.popup,
            "trigger-action",
            (event: { action: { id: string } }) => onTriggerActionRef.current?.(event)
        );
        return () => handle.remove();
    }, [map]);

    const onDeleteConfirmation = (confirmed: boolean) => {
        const feature = pendingDeleteFeature;
        setPendingDeleteFeature(null);
        if (confirmed && feature) {
            logFirebaseEvent("nff_delete_feature");
            dispatch(cdActions.deleteNonFieldFeature(feature));
        }
    };

    if (!pendingDeleteFeature) {
        return null;
    }
    return (
        <DeleteConfDialog
            deleteConfirmationTitle={globalMessages.confirmTitle}
            deleteConfirmationMessage={contextMenuMessages.deleteNonFieldFeatureConfirmation}
            onConfirmationClick={onDeleteConfirmation}
        />
    );
};
